import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useState } from "react";

import { Alert, Button, CircularProgress, Grid, Snackbar } from "@mui/material";
import { DateTime } from "luxon";

import { ModuleSubscription } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";

import { updateTrialDate } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DatePicker from "components/shared/date-picker";
import {
  DEFAULT_SNACKBAR_DURATION,
  MODULE_ACTION_BUTTON_MIN_WIDTH,
  MODULE_TRIAL_INDEPENDENT_MESSAGE,
  MODULE_TRIAL_MAX_EXTENSION_MESSAGE,
} from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { convertTimestampToDate, convertToTimestamp } from "helpers/time";

interface ChangeModuleTrialDateProps {
  moduleLabel: string;
  moduleSubscription: ModuleSubscription;
  roleId: string;
  regionCode: string;
  onUpdated: (newTrialEndsAt: Timestamp) => void;
}

const ChangeModuleTrialDate = ({
  moduleLabel,
  moduleSubscription,
  roleId,
  regionCode,
  onUpdated,
}: ChangeModuleTrialDateProps) => {
  const initialDate = moduleSubscription.trialEndsAt
    ? convertTimestampToDate(moduleSubscription.trialEndsAt)
    : new Date();

  const [open, setOpen] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [newTrialDate, setNewTrialDate] = useState<Date>(initialDate);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  // Bounded to whole calendar days - not the current instant - since the picker preserves
  // the selected date's own time-of-day (see DatePicker's onDateChange), which would
  // otherwise make picking today or the last allowed day fail a same-day comparison.
  const minDate = DateTime.now().startOf("day").toJSDate();
  // Caps how far a trial can be pushed out in one go, so it can't be repeatedly
  // extended a few days at a time to run indefinitely.
  const maxDate = DateTime.now().plus({ months: 3 }).endOf("day").toJSDate();

  const isSubmitDisabled =
    !DateTime.fromJSDate(newTrialDate).isValid ||
    newTrialDate < minDate ||
    newTrialDate > maxDate;

  const handleConfirm = () => {
    if (newTrialDate.getTime() === initialDate.getTime()) {
      setOpen(false);
      return;
    }

    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);

    updateTrialDate(
      {
        subscriptionId: moduleSubscription.subscriptionId,
        organizationId: roleId,
        newTrialEndDate: convertToTimestamp(newTrialDate),
      },
      callOptionsHeaders(regionCode, moduleSubscription.subscriptionId),
    )
      .then((respEndTrialDate) => {
        if (respEndTrialDate) {
          onUpdated(respEndTrialDate);
        }
        setOpen(false);
        setOpenSnackbar(true);
      })
      .catch((error) => {
        setOpen(false);
        showError(error);
      })
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  return (
    <>
      {showLoadingSpinner ? (
        <CircularProgress size={20} sx={{ mt: 1, mr: 2 }} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 1, mr: 2, minWidth: MODULE_ACTION_BUTTON_MIN_WIDTH }}
          disabled={isSubscriptionActionLoading}
          data-testid={`${moduleSubscription.moduleName}-change-trial-end-date`}
          onClick={() => {
            setNewTrialDate(initialDate);
            setOpen(true);
          }}
        >
          CHANGE TRIAL END DATE
        </Button>
      )}

      <ConfirmDialog
        title={`Update ${moduleLabel} Trial End Date`}
        open={open}
        width="sm"
        setOpen={setOpen}
        disabled={isSubmitDisabled}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        onConfirm={handleConfirm}
      >
        <Grid container justifyContent="flex-start" spacing={1}>
          <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
            <Alert
              severity="info"
              data-testid="module-trial-date-independent-banner"
            >
              {MODULE_TRIAL_INDEPENDENT_MESSAGE}
            </Alert>
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <Alert
              severity="info"
              data-testid="module-trial-date-max-extension-banner"
            >
              {MODULE_TRIAL_MAX_EXTENSION_MESSAGE}
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              data-testid="date-picker-input-field"
              defaultDateSelection={newTrialDate}
              minDate={minDate}
              maxDate={maxDate}
              onChange={setNewTrialDate}
            />
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setOpenSnackbar(false)}
        data-testid={`snackbar-change-${moduleSubscription.moduleName}-trial-date`}
      >
        <Alert severity="success" variant="filled">
          Trial end date updated
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangeModuleTrialDate;
